import { onReady, scrollIntoView, isMobileView } from "/Assets/js/common/utilities.js";

const logError = message => window.console.error(`NavigationMenu: ${message}`);

onReady(() => {
    const nav = document.querySelector("nav.navbar");
    if (nav) {
        const navigationMenu = new NavigationMenu(nav);
        navigationMenu.init();
    }
});

class NavigationMenu {
    constructor(nav, options = {}) {
        this.nav = nav;
        this.opts = {
            dropdownSelector: "li.dropdown",
            expandingHeaderSelector: "button.expanding-list",
            flyoutHeaderSelector: "button.flyout",
            focusOnOpen: false,
            ...options
        };

        this.dropdowns = [];
        this.initialized = false;

        // Bind handlers
        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleDocumentClick = this.handleDocumentClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleFlyoutClick = this.handleFlyoutClick.bind(this);
        this.handleExpandingListClick = this.handleExpandingListClick.bind(this);
    }

    // ----- Initialization / Destruction -----
    init() {
        if (!this.nav) {
            logError("Root navigation element not found.");
            return;
        }
        if (this.initialized) return;

        this.cacheDom();
        this.setupDropdownTriggers();
        this.setupExpandingHeaders();
        this.setupFlyoutHeaders();

        document.addEventListener("click", this.handleDocumentClick, { capture: true });
        document.addEventListener("keydown", this.handleKeydown);

        this.initialized = true;
    }

    cacheDom() {
        this.dropdowns = Array.from(this.nav.querySelectorAll(this.opts.dropdownSelector));
        this.expandingLists = this.nav.querySelectorAll(this.opts.expandingHeaderSelector);
        this.flyoutMenus = this.nav.querySelectorAll(this.opts.flyoutHeaderSelector);
    }

    setupDropdownTriggers() {
        if (!this.dropdowns.length) return;
        this.dropdowns.forEach(li => {
            const trigger = li.querySelector("a, button, span");
            if (!trigger) return;
            trigger.addEventListener("click", this.handleTriggerClick);
        });
    }

    setupExpandingHeaders() {
        this.expandingLists.forEach(header => {
            header.addEventListener("click", this.handleExpandingListClick);
        });
    }

    setupFlyoutHeaders() {
        this.flyoutMenus.forEach(header => {
            header.addEventListener("click", this.handleFlyoutClick);
        });
    }

    // ----- Event Handlers -----
    handleFlyoutClick(e) {
        e.preventDefault();
        e.stopPropagation();
        if (this.isModifiedClick(e) || !this.flyoutMenus) return;

        const header = e.currentTarget;
        const wasExpanded = header.classList.contains("open");

        // Mutual exclusion: close expanding lists first
        this.closeExpandingLists();
        this.closeFlyouts();

        if (!wasExpanded) {
            this.openFlyout(header);
        }
    }

    handleExpandingListClick(e) {
        e.preventDefault();
        e.stopPropagation();
        if (this.isModifiedClick(e)) return;

        const header = e.currentTarget;
        const wasExpanded = header.classList.contains("open");

        // Mutual exclusion: close flyouts first
        this.closeFlyouts();
        this.closeExpandingLists();

        if (!wasExpanded) {
            this.openExpandingList(header);
        }
    }

    handleTriggerClick(e) {
        e.preventDefault();
        e.stopPropagation();
        if (this.isModifiedClick(e)) return;

        const li = e.currentTarget.closest(this.opts.dropdownSelector);
        if (!li) return;

        const isOpen = li.classList.contains("open");
        this.closeAll();
        if (!isOpen) {
            this.open(li);

            if (isMobileView()) {
                scrollIntoView(li, { behavior: "smooth" }, 0, true);
            }
        }
    }

    handleDocumentClick(e) {
        if (!this.nav.contains(e.target)) {
            this.closeAll();
        }
    }

    handleKeydown(e) {
        // ESC closes everything
        if (e.key === "Escape" || e.key === "Esc") {
            this.closeAll();
            return;
        }

        // Arrow navigation for headers (basic enhancement)
        const focusEl = document.activeElement;
        if (!this.nav.contains(focusEl)) return;

        const headers = [
            ...this.expandingLists,
            ...this.flyoutMenus
        ];
        if (!headers.length) return;

        const idx = headers.indexOf(focusEl);
        if (idx === -1) return;

        if (e.key === "ArrowDown" || e.key === "ArrowRight") {
            e.preventDefault();
            const next = headers[(idx + 1) % headers.length];
            next.focus();
        } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
            e.preventDefault();
            const prev = headers[(idx - 1 + headers.length) % headers.length];
            prev.focus();
        } else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            focusEl.click();
        }
    }

    // ----- Utility / State Methods -----
    isModifiedClick(e) {
        return e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
    }

    getTrigger(li) {
        return li.querySelector('a[aria-haspopup="true"], button[aria-haspopup="true"]');
    }

    // Dropdown (legacy style)
    open(li) {
        li.classList.add("open");
        const trigger = this.getTrigger(li);
        if (trigger) trigger.setAttribute("aria-expanded", "true");
    }

    close(li) {
        li.classList.remove("open");
        const trigger = this.getTrigger(li);
        if (trigger) trigger.setAttribute("aria-expanded", "false");
    }

    // Expanding list logic
    openExpandingList(header) {
        header.classList.add("open");
        header.setAttribute("aria-expanded", "true");

        const panel = header.nextElementSibling;
        if (panel) panel.classList.add("open");
        if (this.opts.focusOnOpen) this.focusFirstIn(panel);
    }

    closeExpandingLists() {
        if (!this.expandingLists) return;
        this.expandingLists.forEach(header => {
            header.classList.remove("open");
            header.setAttribute("aria-expanded", "false");

            const panel = header.nextElementSibling;
            if (panel) panel.classList.remove("open");
        });
    }

    // Flyout logic
    openFlyout(flyout) {
        flyout.classList.add("open");
        flyout.setAttribute("aria-expanded", "true");
        const flyoutId = flyout.getAttribute("data-flyout");
        const closestParentWithClass = flyout.closest(".dropdown-flyout-menu");
        const flyoutContent = closestParentWithClass?.querySelector(`.flyout-menu-content[data-flyout="${flyoutId}"]`);

        if (flyoutContent) {
            flyoutContent.classList.add("open");
            const parent = flyoutContent.parentElement;
            // Add open to parent container if present (per requirement)
            if (parent && !parent.classList.contains("open")) {
                parent.classList.add("open");
            }
        }
        if (this.opts.focusOnOpen) this.focusFirstIn(flyoutContent);
    }

    closeFlyouts() {
        if (!this.flyoutMenus) return;
        this.flyoutMenus.forEach(flyout => {
            flyout.classList.remove("open");
            flyout.setAttribute("aria-expanded", "false");
            const flyoutId = flyout.getAttribute("data-flyout");
            const closestParentWithClass = flyout.closest(".dropdown-flyout-menu");
            const flyoutContent = closestParentWithClass?.querySelector(`.flyout-menu-content[data-flyout="${flyoutId}"]`);

            if (flyoutContent) {
                flyoutContent.classList.remove("open");
                const parent = flyoutContent.parentElement;
                if (parent) parent.classList.remove("open");
            }
        });
    }

    focusFirstIn(container) {
        if (!container) return;
        const focusable = container.querySelector(
            'a[href], button:not([disabled]), [role="menuitem"], [tabindex]:not([tabindex="-1"])'
        );
        if (focusable) focusable.focus();
    }

    closeAll(except = null) {
        // Dropdowns
        this.dropdowns.forEach(li => {
            if (except && li === except) return;
            this.close(li);
        });
        // Structured groups
        this.closeExpandingLists();
        this.closeFlyouts();
    }

    // ----- Destruction -----
    destroy() {
        if (!this.initialized) return;

        // Dropdown triggers
        this.dropdowns.forEach(li => {
            const trigger = this.getTrigger(li);
            if (trigger) {
                trigger.removeEventListener("click", this.handleTriggerClick);
                trigger.removeAttribute("aria-haspopup");
                trigger.removeAttribute("aria-expanded");
            }
        });

        if (this.expandingLists) {
            this.expandingLists.forEach(header => {
                header.removeEventListener("click", this.handleExpandingListClick);
                header.removeAttribute("aria-controls");
                header.removeAttribute("aria-expanded");
                header.removeAttribute("role");
            });
        }

        if (this.flyoutMenus) {
            this.flyoutMenus.forEach(header => {
                header.removeEventListener("click", this.handleFlyoutClick);
                header.removeAttribute("aria-controls");
                header.removeAttribute("aria-expanded");
                header.removeAttribute("role");
            });
        }

        document.removeEventListener("click", this.handleDocumentClick, { capture: true });
        document.removeEventListener("keydown", this.handleKeydown);

        this.initialized = false;
    }
}